/**
 * Per-tab persistence of the AI Analyst's open/closed state and current chat
 * thread, scoped to the file currently open in this tab.
 *
 * Stored in sessionStorage (not localStorage) so the state survives a page
 * refresh in the same tab but does not leak to other tabs: a brand-new tab
 * gets a fresh session and falls back to the "Show AI on startup" preference.
 */

const SESSION_KEY = 'currentFileAIState';

export type CurrentFileAIState = {
  fileUuid: string;
  aiAnalystOpen: boolean;
  // The id of the open chat thread. An empty string means a blank new chat.
  currentChatId: string;
};

export const readCurrentFileAIState = (): CurrentFileAIState | undefined => {
  try {
    if (typeof window === 'undefined') return undefined;
    const saved = window.sessionStorage.getItem(SESSION_KEY);
    if (!saved) return undefined;
    const parsed = JSON.parse(saved);
    if (
      parsed &&
      typeof parsed.fileUuid === 'string' &&
      typeof parsed.aiAnalystOpen === 'boolean' &&
      typeof parsed.currentChatId === 'string'
    ) {
      return parsed;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

export const writeCurrentFileAIState = (state: CurrentFileAIState): void => {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    // Ignore write failures (e.g. storage disabled or quota exceeded).
  }
};
